import React from 'react';
import { Input, Button } from '@ok/okd';
import { InputNumber } from '@ok/okj-common';
import { calc } from '@ok/blade';
import { toLocale } from '@common/i18n';
import utils from '@common/utils';
import { PLACEHOLDER } from '@pages/currencyInfo/common/context';
import URL from '@pages/currencyInfo/common/URL';
import {
  calcGetAmount,
  calcPayAmount,
  formatZero,
} from '@pages/currencyInfo/common/util';

import './index.less';
import classNames from 'classnames';

const { utils: { ont } = {} } = window;

class Exchange extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      getAmountFocus: false,
      payAmountFocus: false,
      payAmount: '',
      getAmount: '',
      payCurrencyName: 'JPY',
      isFiat: true, // for example: JPY、dollar
      data: {},
    };
  }

  onPayAmountChange = (value) => {
    const amount = formatZero(value);
    const { precision, maxPrecision } = this.getPrecision(amount);
    const needTruncation = precision >= maxPrecision;

    this.setState(
      {
        payAmount: needTruncation
          ? calc.floorTruncate(amount, maxPrecision, false)
          : amount,
      },
      () => {
        this.calcGetAmount();
      }
    );
  };

  getPrecision(value = '0') {
    const defaultCryptoPrecision = 8;
    const { isFiat, data } = this.state;
    const { quotePrecision } = data; // BE interface return fiat quotePrecision
    const precision = calc.digitLength(value);
    const maxPrecision = isFiat ? quotePrecision : defaultCryptoPrecision;
    return {
      precision,
      maxPrecision,
    };
  }

  iconUrl = (currencyName) => {
    return `${
      window.okGlobal.cdnBaseUrl
    }/cdn/assets/imgs/icon/${currencyName.toLowerCase()}_60.png`;
  };

  updatePayAmount = () => {
    const { isFiat, getAmount, data } = this.state;
    const { latestPrice } = this.state.data;
    const { quotePrecision } = data; // BE interface return fiat quotePrecision
    this.setState({
      payAmount: calcPayAmount({
        isFiat,
        getAmount,
        exchangePrice: latestPrice,
        precision: quotePrecision,
      }),
    });
  };

  calcGetAmount = () => {
    const { isFiat, payAmount, data } = this.state;
    const { latestPrice } = data;
    const defaultBuyPrecision = 8;
    const precision = defaultBuyPrecision;
    this.setState({
      getAmount: calcGetAmount({
        isFiat,
        payAmount,
        exchangePrice: latestPrice,
        precision,
      }),
    });
  };

  onGetAmountChange = (value) => {
    const amount = formatZero(value);
    const { precision, maxPrecision } = this.getPrecision(amount);
    const needTruncation = precision >= maxPrecision;

    this.setState(
      {
        getAmount: needTruncation
          ? calc.floorTruncate(amount, maxPrecision, false)
          : amount,
      },
      this.updatePayAmount
    );
  };

  handleClickFocus = (type, isFocused) => {
    this.setState({ [type]: isFocused });
  };

  handleInputType = (value) => {
    this.setState({
      isFiat: value,
    });
  };

  componentDidMount() {
    this.timer = setInterval(() => {
      this.fetchPriceExchange();
    }, 5000);
    this.fetchPriceExchange();
  }

  async fetchPriceExchange() {
    const { currency } = this.props;
    const currencyId = currency.id;
    const data = await ont.get(
      `${URL.GET_CURRENCY_LATEST_PRICE}?estCurrencyId=${currencyId}`
    );
    this.setState({ data: data.data.data });
  }

  componentWillUnmount() {
    clearInterval(this.timer);
  }

  render() {
    const { currency } = this.props;
    const currencyName = currency?.symbol?.toUpperCase() || '';
    const {
      payAmount,
      getAmount,
      data,
      payCurrencyName,
      isFiat,
      payAmountFocus,
      getAmountFocus,
    } = this.state;
    const { title } = this.props;
    const { latestPrice } = data;
    const hashMount = isFiat
      ? `payAmount=${payAmount}`
      : `getAmount=${getAmount}`;

    const price = utils.isNumber(latestPrice)
      ? calc.thousandFormat(latestPrice)
      : PLACEHOLDER;

    return (
      <div className="exchange-box">
        <div className="exchange-title">{title}</div>
        <div className="exchange-content">
          <div className={classNames('pay-input', { focused: payAmountFocus })}>
            <div className="form-title">
              {toLocale('currency_buy_calculator_pay')}
            </div>
            <div className="form-input">
              <InputNumber
                className="input-number"
                innerClassName="form-inner-input"
                value={payAmount}
                size={Input.SIZE.sm}
                onChange={this.onPayAmountChange}
                onFocus={() => {
                  this.handleClickFocus('payAmountFocus', true);
                  return this.handleInputType(true);
                }}
                onBlur={() => {
                  this.handleClickFocus('payAmountFocus', false);
                }}
                placeholder="0.00"
                maxLength={15}
              />
              <div className="input-right">
                <img src={this.iconUrl('jpy_fiat')} />
                <span className="unit">JPY</span>
              </div>
            </div>
          </div>
          <div className={classNames('get-input', { focused: getAmountFocus })}>
            <div className="form-title">
              {toLocale('currency_buy_calculator_get')}
            </div>
            <div className="form-get">
              <InputNumber
                className="input-number"
                innerClassName="form-inner-input"
                value={getAmount}
                size={Input.SIZE.sm}
                onChange={this.onGetAmountChange}
                onFocus={() => {
                  this.handleClickFocus('getAmountFocus', true);
                  return this.handleInputType(false);
                }}
                onBlur={() => {
                  this.handleClickFocus('getAmountFocus', false);
                }}
                placeholder="0.00"
                maxLength={12}
              />
              <div className="input-right">
                <img src={this.iconUrl(currencyName.toLowerCase())} />
                <span className="unit">{currencyName}</span>
              </div>
            </div>
          </div>
          <div className="exchange-tips">
            {`1 ${currencyName} ≈ ${price} ${payCurrencyName} `}
          </div>
          <div className="button">
            <Button
              category={Button.CATEGORY.fill}
              type={Button.TYPE.primary}
              size={Button.SIZE.lg}
              className="exchange-button"
              href={`/sale/${currencyName}#${hashMount}`}
              onClick={() => {
                window.seoMonitor.act(
                  'web_token_information_buysell_buy_click'
                );
              }}
            >
              {toLocale('currency_buy_calculator_button')}
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export default Exchange;
